import React from "react";
import { Link } from "react-router-dom";
import useForceLightTheme from "../../hooks/useForceLightTheme";
import vcFavIcon from "../../Assets/Images/Logos/simple_logo.png";
import vcLogo from "../../Assets/Images/Logos/logo.png";
import lmsLogoImg from "../../Assets/Images/Logos/lms_logo.png";
import "./TermsAndConditions.scss";

const isLmsCampus = () => {
  const h = window.location.hostname;
  return h.endsWith('lmscampus.com') || h === 'localhost';
};
const favIcon = isLmsCampus() ? lmsLogoImg : vcFavIcon;
const logo = isLmsCampus() ? lmsLogoImg : vcLogo;

const TermsAndConditions = () => {
  useForceLightTheme();

  return (
    <div className="terms-page">
      {/* Header */}
      <header className="terms-header">
        <div className="terms-header-inner">
          <Link to="/login" className="terms-logo-link">
            <img src={favIcon} alt="Logo" className="terms-logo-icon" />
            <span className="terms-logo-text">LMS Campus</span>
          </Link>
          <div className="terms-header-actions">
            <Link to="/login" className="terms-header-btn terms-header-btn--outline">
              Sign In
            </Link>
            <Link to="/signup" className="terms-header-btn terms-header-btn--primary">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="terms-hero">
        <h1>Terms & Conditions</h1>
        <p>Last updated: February 20, 2026</p>
      </div>

      {/* Content */}
      <div className="terms-content">
        <div className="terms-card">
          <section className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using the LMS Campus platform ("Service"), you acknowledge that you
              have read, understood, and agree to be bound by these Terms and Conditions ("Terms"). If
              you do not agree with any part of these Terms, you must not use our Service.
            </p>
            <p>
              These Terms apply to all visitors, users, students, instructors, administrators, and
              others who access or use the Service.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Description of Service</h2>
            <p>
              LMS Campus is a virtual classroom and learning management platform that provides:
            </p>
            <ul>
              <li>Live interactive online classes and virtual classrooms</li>
              <li>Course management and syllabus tracking</li>
              <li>Assignment creation, submission, and grading</li>
              <li>AI-powered learning tools and practice sessions</li>
              <li>Mock interview preparation and practice</li>
              <li>Student performance tracking and attendance management</li>
              <li>Fee collection and payment processing</li>
              <li>Class recordings and educational content delivery</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>3. User Accounts</h2>
            <h3>3.1 Registration</h3>
            <p>
              To use certain features of the Service, you must register for an account. You agree to
              provide accurate, current, and complete information during registration and to update
              such information to keep it accurate, current, and complete.
            </p>
            <h3>3.2 Account Security</h3>
            <p>
              You are responsible for safeguarding your password and for all activities that occur
              under your account. You agree to notify us immediately of any unauthorized use of your
              account. We will not be liable for any loss or damage arising from your failure to
              protect your account credentials.
            </p>
            <h3>3.3 Account Types</h3>
            <p>
              The Service supports multiple user roles including Students, Instructors, and
              Administrators. Each role has specific permissions and responsibilities within the
              platform.
            </p>
          </section>

          <section className="terms-section">
            <h2>4. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable local, state, national, or international law</li>
              <li>
                Transmit any material that is defamatory, offensive, or otherwise objectionable
              </li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>
                Interfere with or disrupt the Service or servers connected to the Service
              </li>
              <li>
                Attempt to gain unauthorized access to other user accounts or computer systems
              </li>
              <li>Upload viruses, malware, or other malicious code</li>
              <li>
                Share account credentials or allow multiple individuals to use a single account
              </li>
              <li>
                Copy, distribute, or disclose any part of the Service in any medium without
                authorization
              </li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>5. Intellectual Property</h2>
            <h3>5.1 Platform Content</h3>
            <p>
              The Service and its original content (excluding content provided by users), features,
              and functionality are and will remain the exclusive property of LMS Campus and its
              licensors. The Service is protected by copyright, trademark, and other laws.
            </p>
            <h3>5.2 User Content</h3>
            <p>
              Users retain ownership of content they create and upload to the platform, including
              assignments, course materials, and educational resources. By uploading content, you
              grant LMS Campus a non-exclusive license to use, display, and distribute such
              content within the platform for educational purposes.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Payments & Fees</h2>
            <p>
              Certain features of the Service may require payment of fees. All fees are stated in
              Indian Rupees (INR) unless otherwise specified. Payment processing is handled securely
              through authorized payment gateways. Refund policies are determined by individual
              institutes and course providers on the platform.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Privacy Policy</h2>
            <p>
              Your privacy is important to us. Our collection and use of personal information in
              connection with the Service is described in our Privacy Policy. By using the Service, you
              consent to the collection and use of information as outlined therein.
            </p>
            <h3>7.1 Data We Collect</h3>
            <ul>
              <li>Personal information: name, email address, phone number</li>
              <li>Academic information: course enrollments, grades, attendance records</li>
              <li>Usage data: login activity, feature usage, session recordings</li>
              <li>Payment information: transaction details (processed securely via payment gateways)</li>
            </ul>
            <h3>7.2 How We Use Your Data</h3>
            <ul>
              <li>To provide and maintain the Service</li>
              <li>To personalize the learning experience</li>
              <li>To communicate updates, notifications, and reminders</li>
              <li>To process payments and manage fee records</li>
              <li>To improve the platform and develop new features</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>8. Class Recordings & Content</h2>
            <p>
              Live classes may be recorded for educational purposes. Participants will be informed
              when recording is active. Recorded content is made available to enrolled students and
              authorized users only. Unauthorized sharing or distribution of recorded content is
              strictly prohibited.
            </p>
          </section>

          <section className="terms-section">
            <h2>9. AI-Powered Features</h2>
            <p>
              The Service includes AI-powered learning tools, including but not limited to AI
              practice sessions, mock interviews, and automated assessments. While we strive for
              accuracy, AI-generated content and evaluations should be used as supplementary learning
              tools and not as a sole measure of academic performance.
            </p>
          </section>

          <section className="terms-section">
            <h2>10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, LMS Campus shall not be liable
              for any indirect, incidental, special, consequential, or punitive damages, or any loss
              of profits or revenues, whether incurred directly or indirectly, or any loss of data,
              use, goodwill, or other intangible losses.
            </p>
          </section>

          <section className="terms-section">
            <h2>11. Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without
              prior notice or liability, for any reason, including without limitation if you breach
              these Terms. Upon termination, your right to use the Service will immediately cease.
            </p>
          </section>

          <section className="terms-section">
            <h2>12. Changes to Terms</h2>
            <p>
              We reserve the right to modify or replace these Terms at any time. If a revision is
              material, we will provide at least 30 days' notice prior to any new terms taking
              effect. What constitutes a material change will be determined at our sole discretion.
              Your continued use of the Service after changes become effective constitutes acceptance
              of the revised Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>13. Contact Us</h2>
            <p>
              If you have any questions about these Terms & Conditions, please contact us at:
            </p>
            <div className="terms-contact-card">
              <p><strong>LMS Campus</strong></p>
              <p>Email: support@visiblecampus.com</p>
              <p>Website: www.visiblecampus.com</p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="terms-footer">
        <div className="terms-footer-inner">
          <img src={logo} alt="LMS Campus" className="terms-footer-logo" />
          <p>&copy; {new Date().getFullYear()} LMS Campus. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditions;
